import { isPublicPath } from './publicPaths.js';
/**
 * Thin fetch wrapper.
 *
 * Auth is the HttpOnly session cookie, so there is no token handling in the
 * browser at all — nothing to steal via XSS, nothing to store. `credentials:
 * 'same-origin'` sends it; the X-Requested-With header satisfies the API's
 * CSRF check on state-changing methods.
 */

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error ?? `Request failed (${status})`);
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = 'GET', body, signal } = {}) {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(body ? { 'Content-Type': 'application/json' } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });

  if (res.status === 401) {
    // Session expired or never existed. Bounce through Entra, preserving
    // where the user was trying to go.
    //
    // Two guards, because without them a *rejected* sign-in loops forever and
    // hides its own reason: the server sends the user to /error, the page
    // calls an API, gets 401, bounces to /auth/signin, Entra re-authenticates
    // silently, the server rejects again. The user sees the browser spinning
    // and no message.
    // Some 401s cannot be fixed by signing in again - loadUser destroys the
    // session and rejects, so a retry re-authenticates with Entra, succeeds,
    // and lands on the same rejection. Read the reason before deciding.
    const body = await res.json().catch(() => null);
    const code = body?.details?.code;
    if (code === 'account_inactive' || code === 'no_user_context') {
      window.location.href = `/error?error=${encodeURIComponent(code)}`;
      throw new ApiError(401, body);
    }

    /* On a public route, being unauthenticated is the normal state, not an
       error. Bouncing to Entra from one is what sent a visitor clicking "Try
       the demo" to a Microsoft sign-in prompt, and what created the sign-in
       loop at the root: silent SSO re-presents the same refused account
       forever. Let the page render instead. */
    if (isPublicPath(window.location.pathname)) {
      throw new ApiError(401, { error: 'unauthenticated' });
    }

    const onErrorPage = window.location.pathname.startsWith('/error');

    // A second bounce within a few seconds is a loop, not a stale session:
    // a genuine re-auth only needs one.
    let looping = false;
    try {
      const last = Number(sessionStorage.getItem('vz-auth-bounce') || 0);
      looping = Date.now() - last < 10_000;
      sessionStorage.setItem('vz-auth-bounce', String(Date.now()));
    } catch { /* private mode: fall back to a single bounce */ }

    if (onErrorPage || looping) {
      if (looping && !onErrorPage) window.location.href = '/error?error=signin_loop';
      throw new ApiError(401, { error: 'unauthenticated' });
    }

    const returnTo = window.location.pathname + window.location.search;
    window.location.href = `/auth/signin?returnTo=${encodeURIComponent(returnTo)}`;
    throw new ApiError(401, { error: 'unauthenticated' });
  }

  const payload = res.headers.get('content-type')?.includes('application/json')
    ? await res.json().catch(() => null)
    : null;

  if (!res.ok) throw new ApiError(res.status, payload);
  return payload;
}

export const api = {
  get:  (path, opts) => request(path, { ...opts }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  put:  (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  del:  (path, opts) => request(path, { ...opts, method: 'DELETE' })
};

/* ---------------------------- endpoints ---------------------------- */

export const getMe            = () => api.get('/api/me');
export const getAiStatus      = () => api.get('/ai/status');
export const savePreferences  = (prefs) => api.put('/api/me/preferences', prefs);
export const getNavigation    = (surface = 'Desktop') => api.get(`/api/navigation?surface=${surface}`);

export const getReportEmbed    = (id) => api.get(`/api/embed/report/${id}`);
export const getPaginatedEmbed = (id) => api.get(`/api/embed/paginated/${id}`);
export const getDashboardEmbed = (id) => api.get(`/api/embed/dashboard/${id}`);
export const getAppToken       = (profileItemId) => api.post('/api/embed/app-token', { profileItemId });
export const recordPageView    = (profileItemId) => api.post('/api/telemetry/page-view', { profileItemId });

export const addFavourite    = (id) => api.put(`/api/favourites/${id}`);
export const removeFavourite = (id) => api.del(`/api/favourites/${id}`);

export const getVisualSettings    = (reportId) => api.get(`/api/reports/${reportId}/visual-settings`);
export const saveVisualSettings   = (reportId, slicerStates) =>
  api.put(`/api/reports/${reportId}/visual-settings`, { slicerStates });
export const clearVisualSettings  = (reportId) => api.del(`/api/reports/${reportId}/visual-settings`);

export const signOut = async () => {
  const { logoutUrl } = await api.post('/auth/signout');
  window.location.href = logoutUrl;
};

/** Vizora staff console. The server 404s anyone not in the Entra platform
    group, so these are unreachable for customer administrators. */
export const platform = {
  getContext:     () => api.get('/api/platform/context'),
  listCustomers:  () => api.get('/api/platform/customers'),
  getCustomer:    (id) => api.get(`/api/platform/customers/${id}`),
  createCustomer: (data) => api.post('/api/platform/customers', data),
  updateCustomer: (id, data) => api.put(`/api/platform/customers/${id}`, data),
  setActive:      (id, isActive) =>
    api.post(`/api/platform/customers/${id}/active`, { isActive }),
  // Every tenant's AI spend. Staff only; non-staff get a 404 from the API.
  getAiUsage:     (months = 6) => api.get(`/api/platform/ai-usage?months=${months}`),
  // Report usage across every tenant. Staff only.
  getUsage:       (days = 30) => api.get(`/api/platform/usage?days=${days}`),

  /* Customer logos. Uploaded as base64 in JSON rather than multipart: one
     small image does not justify a file-upload dependency, and the server
     validates the decoded BYTES rather than anything the browser declares. */
  listLogos:      (id) => api.get(`/api/platform/customers/${id}/logos`),
  uploadLogo:     (id, body) => api.put(`/api/platform/customers/${id}/logos`, body),
  deleteLogo:     (id, variant) =>
    api.del(`/api/platform/customers/${id}/logos/${variant}`)
};

export const admin = {
  // Scoped server-side to the caller's own customer.
  getAiUsage:   (months = 12) => api.get(`/api/admin/ai-usage?months=${months}`),
  /* The monthly spend cap. amount may be null (no cap) or 0 (a real cap of
     zero) - the two are different outcomes, so the caller must not collapse an
     empty field to 0 on its way here. */
  saveAiCap:    (body) => api.put('/api/admin/ai-cap', body),
  // Who has opened which report. Scoped server-side to the caller's customer.
  getUsage:     (days = 30) => api.get(`/api/admin/usage?days=${days}`),

  listUsers:    () => api.get('/api/admin/users'),
  getUser:      (id) => api.get(`/api/admin/users/${id}`),
  createUser:   (data) => api.post('/api/admin/users', data),
  updateUser:   (id, data) => api.put(`/api/admin/users/${id}`, data),
  deleteUser:   (id) => api.del(`/api/admin/users/${id}`),

  /* Admin-menu applications. The list is configuration, not data, so it is
     the same for every customer - but it is fetched rather than compiled in,
     because it changes with an app setting and a restart, not a release. */
  listApps:     () => api.get('/api/admin/apps'),
  getAppToken:  (key) => api.post(`/api/admin/apps/${encodeURIComponent(key)}/token`),

  listProfiles:  () => api.get('/api/admin/profiles'),
  getProfile:    (id) => api.get(`/api/admin/profiles/${id}`),
  createProfile: (data) => api.post('/api/admin/profiles', data),
  updateProfile: (id, data) => api.put(`/api/admin/profiles/${id}`, data),
  duplicateProfile: (id) => api.post(`/api/admin/profiles/${id}/duplicate`),

  // View as profile. Adopts a profile's entitlements without changing who the
  // session is: writes stay attributed to the real signer.
  startPreview: (profileId) => api.post('/api/admin/preview', { profileId }),
  endPreview:   () => api.del('/api/admin/preview'),
  deleteProfile: (id) => api.del(`/api/admin/profiles/${id}`),

  getCatalogue:  () => api.get('/api/admin/catalogue'),
  syncCatalogue: () => api.post('/api/admin/catalogue/sync'),

  getPaginatedDatasets:  () => api.get('/api/admin/paginated-datasets'),
  savePaginatedDatasets: (mappings) => api.put('/api/admin/paginated-datasets', { mappings }),

  getAudit: () => api.get('/api/admin/audit'),
  getDiagnostics: () => api.get('/api/admin/diagnostics')
};
